import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { ThreeDots } from 'react-loader-spinner';
import DashBoardSidebar from '../../layouts/dashBoard-Sidebar';
import DashboardNavbar from '../../layouts/dashboardNavbar';
import PaginatedItems from './pagination';
import { RetakeReportAPI, retakeReportCSV } from '../../api/server';
import { downloadCSVBlob } from '../../common/downloadCSV';
import DashboardFilterSelect from './DashboardFilterSelect';
import SetupBirthDatePicker from './SetupBirthDatePicker';
import './RetakeReport.css';

const initialFilters = { range: '3Month', course: 'CC', startDate: '', endDate: '' };
const emptyReport = { result: [], total: 0 };

export default function RetakeReport() {
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(emptyReport);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setData(emptyReport);
    RetakeReportAPI(page, filters).then(res => {
      if (active) setData(res.data.data);
    }).catch(err => {
      if (active) setError(err.response?.data?.error || 'Unable to load the retake report. Please try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, page]);

  const validDates = value => value.startDate && value.endDate && value.startDate <= value.endDate;
  const updateFilter = event => {
    const next = { ...draft, [event.target.name]: event.target.value };
    if (event.target.name === 'range') { next.startDate = ''; next.endDate = ''; }
    setDraft(next);
    setFormError('');
    if (next.range !== 'Date' || validDates(next)) {
      setPage(1);
      setFilters(next);
      setExportError('');
    }
  };
  const apply = event => {
    event.preventDefault();
    if (!validDates(draft)) { setFormError('Choose a valid start and end date.'); return; }
    setPage(1);
    setFilters({ ...draft });
    setExportError('');
  };
  const exportCSV = async () => {
    setExporting(true);
    setExportError('');
    try {
      const res = await retakeReportCSV(filters);
      downloadCSVBlob(res.data, `Retake-Report-${filters.range}-${filters.course}.csv`);
    } catch (err) {
      setExportError('Unable to export the retake report. Please try again.');
    } finally { setExporting(false); }
  };
  const pendingFilters = JSON.stringify(draft) !== JSON.stringify(filters);

  return (
    <div className="wrapper">
      <DashBoardSidebar />
      <div id="content">
        <DashboardNavbar />
        <section className="manage-students language_wrap retake-report-page">
          {draft.range === 'Date' && <div className="retake-date-range">
            {draft.range === 'Date' && <form className="row align-items-center mt-5" onSubmit={apply}>
              <h6 className="fw-bold text-center col-2"><span className="text_success">Date Range:</span></h6>
              <div className="col-3"><div className="input_wrap" style={{ marginBottom: 0 }}>
                <label className="label-title" htmlFor="retake-start">Start Date</label>
                <SetupBirthDatePicker id="retake-start" name="startDate" label="Choose start date" dateFormat="yyyy-mm-dd" showToday value={draft.startDate}
                  onChange={value => setDraft(current => ({ ...current, startDate: value }))} />
              </div></div>
              <div className="col-2 text-center"><label className="label-title">To</label></div>
              <div className="col-3"><div className="input_wrap" style={{ marginBottom: 0 }}>
                <label className="label-title" htmlFor="retake-end">End Date</label>
                <SetupBirthDatePicker id="retake-end" name="endDate" label="Choose end date" dateFormat="yyyy-mm-dd" showToday value={draft.endDate}
                  onChange={value => setDraft(current => ({ ...current, endDate: value }))} />
              </div></div>
              <div className="col-2 text-center"><button className="btn_brnd" type="submit" disabled={loading}>Submit</button></div>
              {formError && <p className="text-danger mt-2" role="alert">{formError}</p>}
            </form>}
          </div>}
          <div className="card"><div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 retake-toolbar">
              <div className="retake-heading">
                <h3 style={{ fontWeight: 'bold', fontSize: 22, textTransform: 'capitalize' }}>Retake Report</h3>
                {!error && <span className="retake-total" aria-live="polite">Total: <strong>{loading ? '…' : data.total}</strong></span>}
              </div>
              <div className="d-flex justify-content-between align-items-center dasboard flex-wrap gap-3">
                <button type="button" className="btn_brnd" style={{ padding: '6px 30px', fontWeight: 500 }} onClick={exportCSV}
                  disabled={loading || exporting || !!error || !data.total || pendingFilters}>
                  {exporting ? 'Exporting…' : 'Export Data'}
                </button>
                <DashboardFilterSelect style={{ width: 150 }} aria-label="Date range" value={draft.range}
                  onChange={event => updateFilter({ target: { name: 'range', value: event.target.value } })}>
                  <option value="Today">Today</option><option value="Week">Week</option><option value="Month">Month</option>
                  <option value="3Month">3 Month</option><option value="Date">Custom Range</option>
                </DashboardFilterSelect>
                <DashboardFilterSelect style={{ width: 135 }} aria-label="Course" value={draft.course}
                  onChange={event => updateFilter({ target: { name: 'course', value: event.target.value } })}>
                  <option value="CC">CC</option><option value="DE" disabled>DE</option>
                </DashboardFilterSelect>
              </div>
            </div>
            {exportError && <p className="text-danger mt-2" role="alert">{exportError}</p>}
            <div className="retake-table-scroll" tabIndex={0} role="region" aria-label="Scrollable retake report">
              <table className="table table-borderless retake-table" aria-label="Retake enrollments" aria-busy={loading}>
                <thead><tr><th>Name</th><th>Email</th><th>Course</th><th>Enrolled</th><th>Completion</th><th>Certificate</th><th>View</th></tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan="7" style={{ height: 100, textAlign: 'center' }}>
                    <div className="d-flex justify-content-center"><ThreeDots height="40" width="80" color="#4fa94d" ariaLabel="Loading retake enrollments" /></div>
                  </td></tr> : error ? <tr><td colSpan="7" className="text-center"><span role="alert">{error}</span>
                    <button type="button" className="btn btn-link" onClick={() => setFilters({ ...filters })}>Retry</button>
                  </td></tr> : !data.result.length ? <tr><td colSpan="7" className="text-center">No Data Found</td></tr>
                    : data.result.map(row => <tr key={row.retakeId}>
                      <td>{row.name?.trim() || 'N/A'}</td><td>{row.email || 'N/A'}</td><td>CC</td>
                      <td>{row.enrolledAt}</td><td>{row.completion}</td><td>{row.certificate}</td>
                      <td><Link to={`/editstudent?id=${row.userId}`} aria-label={`View ${row.name || 'student'}`}><FaEye style={{ color: 'black' }} /></Link></td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {!loading && !error && data.total > 10 && <PaginatedItems total={data.total} pageNo={page} setPageNo={setPage} pageSize={10} responsive />}
          </div></div>
        </section>
      </div>
    </div>
  );
}
